import React, { useMemo, useState, useEffect } from 'react';
import { Button, Typography, message } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import useBoolean from 'common/hooks/useBoolean';
import { loginContentTypes } from '../login-content';
import {
  QUERY_GET_SEND_PHONE_CODE_LAST_TIME,
  MUTATION_VERIFY_PHONE_CODE,
  MUTATION_GENERATE_CAPTCHA,
  MUTATION_SEND_PHONE_CODE,
  MUTATION_LANDLORD_ACCOUNT_LOGIN,
} from '../queries';
import CaptchaModal, {
  CAPTCHA_SIZE,
  VALIDATION_WIDTH,
  VALIDATION_HEIGHT,
  getCaptchaModalErrorTip,
} from '../captcha-modal';
import { CODE_TYPE } from '~constants/auth';
import { FieldInput, FieldCheckbox, getPhoneNumberModalErrorTip } from '../login-formik-fields';
import { authActionTypes } from '~client/constants/actionTypes';
import event from '~helpers/mixpanel';
import { pageType } from '~constants/pages';
import cookies from 'js-cookie';
import { cookieNames } from '~constants/storage';
import { decodeBase64 } from 'common/helpers/decodeBase64';

const useStyles = makeStyles({
  resendNow: {
    color: '#38B2A6',
    paddingLeft: 8,
    border: 0,
    cursor: 'pointer',
  },
});

const LoginForSetPhoneNumberCode = ({
  loginPhone,
  handleNextContent,
  setSignupValue,
  codeType,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();
  const [phoneCodeForInput, setPhoneCodeForInput] = useState();
  const [newsletterAccepted, setNewsletterAccepted] = useState();

  const [mutationLandlordAccountLogin, { loading: landlordAccountLoginLoading }] = useMutation(
    MUTATION_LANDLORD_ACCOUNT_LOGIN,
    {
      variables: { input: { phone: loginPhone, phoneCode: phoneCodeForInput } },
      onCompleted: successData => {
        const loginData = successData?.landlordAccountLogin;
        const authToken = loginData?.authToken;
        const { company, firstName, lastName } = loginData?.landlordContact || {};
        const partner = company?.name || '';
        const partnerId = decodeBase64(company?.id)?.id || '';
        const userName = `${firstName} ${lastName}` || '';

        cookies.set(cookieNames.partnerId, partnerId);
        cookies.set(cookieNames.partner, partner);
        cookies.set(cookieNames.username, userName);

        event('Successfully Logged In', {
          'Current Page Type': pageType.loginPage,
          'Partner ID': partnerId,
          Partner: partner,
          'User Name': userName,
        });

        dispatch({ type: authActionTypes.LOGGED_IN, token: authToken });
      },
      onError: () => {
        message.error(t('sorry_an_error_occurred_please_try_again'));
      },
    },
  );

  const [verifyPhoneCode, { loading: verifyPhoneCodeLoading }] = useMutation(
    MUTATION_VERIFY_PHONE_CODE,
    {
      onCompleted: e => {
        const verifyPhoneCodeSuccess = e?.verifyPhoneCode?.success;

        if (!verifyPhoneCodeSuccess) {
          message.error(t('sorry_an_error_occurred_please_try_again'));
        }

        if (verifyPhoneCodeSuccess && codeType === CODE_TYPE.LOGIN) {
          mutationLandlordAccountLogin();
        }

        if (verifyPhoneCodeSuccess && codeType === CODE_TYPE.SIGNUP) {
          setSignupValue({
            loginPhone: loginPhone,
            phoneCodeForInput: phoneCodeForInput,
            newsletterAccepted,
          });
          handleNextContent(loginPhone, loginContentTypes.FINISH_PHONE_NUMBER_FOR_SIGNUP);
        }
      },
      onError: () => {
        message.error(t('sorry_an_error_occurred_please_try_again'));
      },
    },
  );

  const [countDown, setCountDown] = useState(0);

  const [queryCheckLandlordExistCompleted] = useLazyQuery(QUERY_GET_SEND_PHONE_CODE_LAST_TIME, {
    variables: { phone: loginPhone, type: codeType },
    onCompleted: e => {
      const lastSendTime = e?.getSendPhoneCodeLastTime?.lastSendTime;
      if (lastSendTime) {
        setCountDown(60);
      } else {
        setCountDown(0);
      }
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    queryCheckLandlordExistCompleted();
  }, [queryCheckLandlordExistCompleted]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (countDown > 0) {
        setCountDown(countDown - 1);
      }
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [countDown, setCountDown]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        phoneCode: yup
          .number()
          .max(9999, t('enter_4_digit_code'))
          .required(t('this_part_cannot_be_empty')),
      }),
    [t],
  );

  const handleSubmit = (values, actions) => {
    if (values?.phoneCode && countDown > 0) {
      verifyPhoneCode({
        variables: {
          input: {
            phone: loginPhone,
            phoneCode: values?.phoneCode,
            type: codeType,
          },
        },
      });
      setPhoneCodeForInput(values?.phoneCode);
      setNewsletterAccepted(values?.newsletterAccepted);
    }

    if (countDown < 1) {
      actions?.setFieldError('phoneCode', t('sorry_the_phone_code_has_expired'));
    }
  };

  const initialValues = useMemo(() => ({ phoneCode: '' }), []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const [captchaErrorTip, setCaptchaErrorTip] = useState('');
  const [open, { setTrue: openModal, setFalse: closeModal }] = useBoolean();

  const [generateCaptcha, { data: captchaValidation, loading: generateCaptchaLoading }] =
    useMutation(MUTATION_GENERATE_CAPTCHA, {
      variables: {
        captchaSize: CAPTCHA_SIZE,
        validationWidth: VALIDATION_WIDTH,
        validationHeight: VALIDATION_HEIGHT,
      },
    });

  const [mutationSendPhoneCode, { loading }] = useMutation(MUTATION_SEND_PHONE_CODE, {
    onCompleted: () => {
      queryCheckLandlordExistCompleted();
      closeModal();
    },
    onError: e => {
      const errMassageText = e?.toString() ?? '';
      const captchaErrorTip = getCaptchaModalErrorTip(errMassageText);

      if (captchaErrorTip) {
        setCaptchaErrorTip(t(captchaErrorTip));
        generateCaptcha();
        return;
      }

      formik?.setSubmitting(false);
      closeModal();
      setCaptchaErrorTip();

      const PhoneNumberModalErrorTip = getPhoneNumberModalErrorTip(errMassageText);
      if (PhoneNumberModalErrorTip) {
        formik?.setErrors({ phone: t(PhoneNumberModalErrorTip) });
        return;
      }

      message.error(t('sorry_an_error_occurred_please_try_again'));
    },
  });

  const closeCaptchaModalAndCreateAccount = async (captchaCode, captchaId) => {
    if (captchaCode && captchaId) {
      await mutationSendPhoneCode({
        variables: {
          captchaCode,
          captchaId,
          phone: loginPhone,
          type: codeType,
        },
      });
    }
  };

  const handleResendNow = () => {
    openModal();
    generateCaptcha();
    formik.setFieldValue('phoneCode', '');
  };

  const handleBackToEmailLogin = () => {
    handleNextContent(null, loginContentTypes.SET_EMAIL);
  };

  useEffect(() => {
    event('Viewed Phone Verification Page', { 'Current Page Type': pageType.loginPage });
  }, []);

  return (
    <FormikProvider value={formik}>
      <Typography style={{ marginTop: 8, marginBottom: 20 }}>
        {t('enter_the_code_we_sent_to_your_phone', { phone: loginPhone })}
      </Typography>
      <FieldInput fieldConfig="phoneCode" placeholder={t('enter_4_digit_code')} />

      {codeType === CODE_TYPE.SIGNUP && (
        <FieldCheckbox
          fieldConfig="newsletterAccepted"
          label={t(
            'like_to_receive_exclusive_offers_and_the_latest_info_from_student_com_by_email_or_sms',
          )}
        />
      )}

      <Button
        type="primary"
        size="large"
        loading={landlordAccountLoginLoading || verifyPhoneCodeLoading}
        onClick={formik.handleSubmit}
      >
        {t('verify')}
      </Button>
      <Typography style={{ marginTop: 40 }}>
        <Typography.Text>{t('do_not_get_the_code')}</Typography.Text>
        {countDown < 1 && (
          <button onClick={handleResendNow} className={classes.resendNow}>
            {t('resend_now')}
          </button>
        )}

        {countDown > 0 && (
          <Typography.Text style={{ color: '#BFBFBF', paddingLeft: 8 }}>
            {t('sent_last_time', { count: countDown })}
          </Typography.Text>
        )}
      </Typography>

      <Typography style={{ marginTop: 24 }}>
        <Typography.Text>{t('or_try')}</Typography.Text>
        <button onClick={handleBackToEmailLogin} className={classes.resendNow}>
          {t('another_login_or_signup_method')}
        </button>
      </Typography>

      {open && (
        <CaptchaModal
          authMethod={codeType === CODE_TYPE.LOGIN ? 'Phone Login' : 'Phone Signup'}
          captchaErrorTip={captchaErrorTip}
          captchaValidation={captchaValidation}
          closeCaptchaModalAndCreateAccount={closeCaptchaModalAndCreateAccount}
          generateCaptcha={generateCaptcha}
          generateCaptchaLoading={generateCaptchaLoading}
          onCloseCaptchaModal={closeModal}
          openCaptchaModal={open}
          setCaptchaErrorTip={setCaptchaErrorTip}
          isLoading={loading}
        />
      )}
    </FormikProvider>
  );
};

LoginForSetPhoneNumberCode.propTypes = {
  loginPhone: PropTypes.string,
  handleNextContent: PropTypes.func.isRequired,
  setSignupValue: PropTypes.func.isRequired,
  codeType: PropTypes.string,
};

export default LoginForSetPhoneNumberCode;
